import React, { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Cinematic rocket-launch intro.
 * - Realistic Falcon-inspired rocket, layered fire + sparks, volumetric smoke.
 * - Screen shake on liftoff, bloom lighting on ignition.
 * - Plays only once per session. Skippable (button / ESC / click / tap).
 * - Respects prefers-reduced-motion.
 */
export default function RocketIntro() {
  // boot -> ignite -> shake -> launch -> fadeout -> done
  const [phase, setPhase] = useState("boot");
  const [visible, setVisible] = useState(false);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem("teuralink_intro_muted") === "1";
    } catch (_) {
      return false;
    }
  });
  const timers = useRef([]);
  const audioRef = useRef(null); // { ctx, master, sources: [] }

  // ---- Rumble sound via Web Audio API ----
  const startRumble = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;

      // Master gain — envelope-driven
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      // Ignite (t≈0.3s): quiet low-end starts
      master.gain.exponentialRampToValueAtTime(0.05, now + 0.4);
      // Shake (t≈0.9s): rumble builds
      master.gain.exponentialRampToValueAtTime(0.14, now + 1.0);
      // Launch (t≈1.2s): peak roar
      master.gain.exponentialRampToValueAtTime(0.22, now + 1.6);
      // Sustained through liftoff
      master.gain.exponentialRampToValueAtTime(0.18, now + 2.6);
      // Fade out with the rocket exiting
      master.gain.exponentialRampToValueAtTime(0.0001, now + 3.4);
      master.connect(ctx.destination);

      // Sub-bass oscillator (deep rumble body)
      const sub = ctx.createOscillator();
      sub.type = "sawtooth";
      sub.frequency.setValueAtTime(38, now);
      sub.frequency.linearRampToValueAtTime(48, now + 2.5);
      const subGain = ctx.createGain();
      subGain.gain.value = 0.55;
      sub.connect(subGain).connect(master);

      // Mid oscillator (thrust body)
      const mid = ctx.createOscillator();
      mid.type = "triangle";
      mid.frequency.setValueAtTime(72, now);
      mid.frequency.linearRampToValueAtTime(96, now + 2.5);
      const midGain = ctx.createGain();
      midGain.gain.value = 0.28;
      mid.connect(midGain).connect(master);

      // Filtered noise (turbulent roar)
      const bufSize = 2 * ctx.sampleRate;
      const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = noiseBuf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuf;
      noise.loop = true;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = "lowpass";
      noiseFilter.frequency.setValueAtTime(180, now);
      noiseFilter.frequency.linearRampToValueAtTime(320, now + 2.5);
      noiseFilter.Q.value = 0.8;
      const noiseGain = ctx.createGain();
      noiseGain.gain.value = 0.65;
      noise.connect(noiseFilter).connect(noiseGain).connect(master);

      // Slow LFO for that "rumbling" wobble on the sub
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = 6.5;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 4;
      lfo.connect(lfoGain).connect(sub.frequency);

      // Start everything
      sub.start(now);
      mid.start(now);
      noise.start(now);
      lfo.start(now);

      // Stop after full envelope
      const endT = now + 3.6;
      sub.stop(endT);
      mid.stop(endT);
      noise.stop(endT);
      lfo.stop(endT);

      // Try to resume if autoplay blocked
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }

      audioRef.current = { ctx, master };
    } catch (_) {
      // Audio unsupported / blocked — silent fail
    }
  };

  const stopRumble = () => {
    try {
      const a = audioRef.current;
      if (!a) return;
      const { ctx, master } = a;
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
      setTimeout(() => {
        try {
          ctx.close();
        } catch (_) {}
      }, 350);
      audioRef.current = null;
    } catch (_) {}
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Skip if already played this session
    try {
      if (sessionStorage.getItem("teuralink_intro_played") === "1") {
        setPhase("done");
        return;
      }
    } catch (_) {}

    // Respect reduced motion
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      try {
        sessionStorage.setItem("teuralink_intro_played", "1");
      } catch (_) {}
      setPhase("done");
      return;
    }

    // Lock scroll during intro
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setVisible(true);

    // Start rumble (unless muted)
    if (!muted) startRumble();

    // Timeline (ms from mount)
    const T = {
      ignite: 300, // engines light, smoke starts, bloom
      shake: 900, // shake begins ~just before liftoff
      launch: 1200, // rocket rises, warp streaks kick in
      fadeout: 3200,
      done: 3900,
    };
    const push = (ms, fn) => timers.current.push(setTimeout(fn, ms));
    push(T.ignite, () => setPhase("ignite"));
    push(T.shake, () => setPhase("shake"));
    push(T.launch, () => setPhase("launch"));
    push(T.fadeout, () => setPhase("fadeout"));
    push(T.done, () => {
      setPhase("done");
      document.body.style.overflow = prevOverflow;
      try {
        sessionStorage.setItem("teuralink_intro_played", "1");
      } catch (_) {}
    });

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      document.body.style.overflow = prevOverflow;
      stopRumble();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    document.body.style.overflow = "";
    try {
      sessionStorage.setItem("teuralink_intro_played", "1");
    } catch (_) {}
    stopRumble();
    setPhase("done");
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    try {
      localStorage.setItem("teuralink_intro_muted", next ? "1" : "0");
    } catch (_) {}
    if (next) {
      stopRumble();
    } else {
      // If a suspended context exists (blocked autoplay), resume it — this
      // click counts as a user gesture in every browser.
      if (audioRef.current?.ctx?.state === "suspended") {
        audioRef.current.ctx.resume().catch(() => {});
      } else if (phase !== "boot" && phase !== "done" && !audioRef.current) {
        startRumble();
      }
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Warp streaks — memoized for stability
  const streaks = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => {
        const left = (i * 137.5) % 100;
        const delay = ((i * 41) % 100) / 100;
        const dur = 0.7 + ((i * 13) % 90) / 100;
        const w = 1 + ((i * 7) % 3);
        return { left, delay, dur, w, id: i };
      }),
    []
  );

  // Twinkling stars
  const stars = useMemo(
    () =>
      Array.from({ length: 110 }).map((_, i) => {
        const top = (i * 53) % 100;
        const left = (i * 89) % 100;
        const size = 1 + ((i * 3) % 3);
        const delay = ((i * 17) % 40) / 10;
        const opacity = 0.35 + ((i * 7) % 55) / 100;
        return { top, left, size, delay, opacity, id: i };
      }),
    []
  );

  if (phase === "done" || !visible) return null;

  const engineOn = phase !== "boot";
  const launched = phase === "launch" || phase === "fadeout";
  const shaking = phase === "shake" || phase === "launch";
  const fading = phase === "fadeout";

  return (
    <div
      data-testid="rocket-intro-overlay"
      onClick={skip}
      className={`fixed inset-0 z-[9999] overflow-hidden cursor-pointer select-none ${
        fading ? "intro-fadeout" : ""
      }`}
      style={{
        background:
          "radial-gradient(ellipse at 50% 115%, #1a1030 0%, #0a0616 45%, #030308 100%)",
      }}
    >
      {/* Bloom lighting from launch pad */}
      <div
        className="absolute pointer-events-none intro-bloom"
        data-active={engineOn ? "1" : "0"}
        data-launched={launched ? "1" : "0"}
      />

      {/* Twinkling stars (background) */}
      <div className="absolute inset-0 pointer-events-none">
        {stars.map((s) => (
          <span
            key={`st-${s.id}`}
            className="absolute rounded-full bg-white"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.opacity,
              animation: `intro-twinkle ${2 + (s.id % 4) * 0.6}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Warp streaks — accelerating field */}
      {launched && (
        <div className="absolute inset-0 pointer-events-none">
          {streaks.map((s) => (
            <span
              key={`w-${s.id}`}
              className="intro-streak"
              style={{
                left: `${s.left}%`,
                width: `${s.w}px`,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Wordmark */}
      <div
        className="absolute left-0 right-0 flex flex-col items-center pointer-events-none"
        style={{ top: "14%" }}
      >
        <div
          className="font-display text-white tracking-tight"
          style={{
            fontSize: "clamp(2rem, 6vw, 4rem)",
            opacity: fading ? 0 : 1,
            transform: fading ? "translateY(-14px)" : "translateY(0)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
            textShadow: "0 0 40px rgba(212,175,55,0.35)",
          }}
        >
          TEURA<span className="text-gold">LINK</span>
        </div>
        <div
          className="overline mt-3"
          style={{
            opacity: fading ? 0 : 0.8,
            transition: "opacity 0.5s ease",
          }}
        >
          {phase === "boot"
            ? "Preflight check"
            : phase === "ignite"
              ? "Ignition sequence start"
              : phase === "shake"
                ? "T-minus zero"
                : "Liftoff"}
        </div>
      </div>

      {/* SCREEN-SHAKE WRAPPER — everything inside vibrates */}
      <div
        className={`absolute inset-0 pointer-events-none ${
          shaking ? "intro-shake" : ""
        } ${launched ? "intro-shake-soft" : ""}`}
      >
        {/* Ground haze at pad */}
        <div className="intro-ground-haze" />

        {/* Volumetric smoke plume — spawns on ignition, stays after launch */}
        {engineOn && (
          <div className="intro-smoke-field">
            {Array.from({ length: 14 }).map((_, i) => (
              <span
                key={`sm-${i}`}
                className="intro-smoke-puff"
                style={{
                  left: `calc(50% + ${(i - 7) * 22}px)`,
                  animationDelay: `${(i % 7) * 0.09}s`,
                  animationDuration: `${1.8 + (i % 5) * 0.25}s`,
                  transform: `translateX(-50%) scale(${0.7 + (i % 4) * 0.15})`,
                }}
              />
            ))}
            {/* Wider low-hanging smoke halo */}
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={`smw-${i}`}
                className="intro-smoke-wide"
                style={{
                  left: `calc(50% + ${(i - 2.5) * 60}px)`,
                  animationDelay: `${0.15 + i * 0.08}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* Rocket rig */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 ${
            launched ? "intro-liftoff" : "intro-idle"
          }`}
          style={{ bottom: "10%", width: "180px" }}
        >
          <div className="relative flex flex-col items-center">
            <RocketSVG />

            {engineOn && (
              <div className="intro-flame-wrap">
                <div className="intro-flame-outer" />
                <div className="intro-flame-mid" />
                <div className="intro-flame-core" />
                {/* Sparks */}
                {Array.from({ length: 10 }).map((_, i) => (
                  <span
                    key={`sp-${i}`}
                    className="intro-spark"
                    style={{
                      left: `${20 + (i * 13) % 60}%`,
                      animationDelay: `${(i * 0.07) % 0.9}s`,
                      animationDuration: `${0.7 + ((i * 11) % 60) / 100}s`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sound toggle */}
      <button
        data-testid="rocket-intro-mute"
        onClick={toggleMute}
        aria-label={muted ? "Unmute launch sound" : "Mute launch sound"}
        className="absolute top-5 right-32 text-xs uppercase tracking-[0.24em] text-white/70 hover:text-white border border-white/15 hover:border-white/40 rounded-full px-3 py-2 transition-colors flex items-center gap-2"
        style={{ backdropFilter: "blur(8px)" }}
      >
        <span aria-hidden="true">
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </span>
        <span className="hidden sm:inline">{muted ? "Sound off" : "Sound on"}</span>
      </button>

      {/* Skip button */}
      <button
        data-testid="rocket-intro-skip"
        onClick={(e) => {
          e.stopPropagation();
          skip();
        }}
        className="absolute top-5 right-5 text-xs uppercase tracking-[0.24em] text-white/70 hover:text-white border border-white/15 hover:border-white/40 rounded-full px-4 py-2 transition-colors"
        style={{ backdropFilter: "blur(8px)" }}
      >
        Skip intro
      </button>

      <style>{`
        @keyframes intro-twinkle {
          0%,100% { opacity: 0.25; }
          50%     { opacity: 1; }
        }

        /* Bloom lighting */
        .intro-bloom {
          left: 50%;
          bottom: -14%;
          width: 90vw;
          height: 55vh;
          transform: translateX(-50%);
          background:
            radial-gradient(ellipse at center,
              rgba(255, 180, 90, 0.35) 0%,
              rgba(255, 138, 31, 0.18) 30%,
              rgba(212, 175, 55, 0.06) 55%,
              rgba(0,0,0,0) 75%);
          filter: blur(24px);
          opacity: 0;
          transition: opacity 0.9s ease;
        }
        .intro-bloom[data-active="1"]   { opacity: 0.85; }
        .intro-bloom[data-launched="1"] { opacity: 0.35; }

        /* Rocket hover before liftoff */
        .intro-idle {
          animation: intro-hover 1.1s ease-in-out infinite alternate;
          will-change: transform;
        }
        @keyframes intro-hover {
          from { transform: translate(-50%, 0); }
          to   { transform: translate(-50%, -4px); }
        }

        /* Liftoff — realistic accelerating curve (slow start, exponential exit) */
        .intro-liftoff {
          animation: intro-liftoff 2.1s cubic-bezier(0.55, 0.02, 0.4, 1) forwards;
          will-change: transform;
        }
        @keyframes intro-liftoff {
          0%   { transform: translate(-50%, 0)      scale(1); }
          10%  { transform: translate(-50%, -2vh)   scale(1.01); }
          30%  { transform: translate(-50%, -18vh)  scale(1); }
          55%  { transform: translate(-50%, -55vh)  scale(0.94); }
          80%  { transform: translate(-50%, -105vh) scale(0.82); }
          100% { transform: translate(-50%, -160vh) scale(0.6); opacity: 0.9; }
        }

        /* Screen shake wrappers */
        .intro-shake {
          animation: intro-shake 0.06s linear infinite;
          will-change: transform;
        }
        .intro-shake-soft {
          animation: intro-shake-soft 0.09s linear infinite;
        }
        @keyframes intro-shake {
          0%   { transform: translate(0, 0); }
          20%  { transform: translate(-2px, 1px); }
          40%  { transform: translate(2px, -1px); }
          60%  { transform: translate(-1px, 2px); }
          80%  { transform: translate(1px, -2px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes intro-shake-soft {
          0%   { transform: translate(0, 0); }
          50%  { transform: translate(-1px, 1px); }
          100% { transform: translate(1px, -1px); }
        }

        /* Ground haze band along the pad */
        .intro-ground-haze {
          position: absolute;
          left: 0; right: 0;
          bottom: 0;
          height: 22vh;
          background: linear-gradient(180deg,
            rgba(0,0,0,0) 0%,
            rgba(180,140,100,0.05) 40%,
            rgba(220,180,140,0.12) 75%,
            rgba(255,220,180,0.18) 100%);
          filter: blur(6px);
          pointer-events: none;
        }

        /* Smoke field */
        .intro-smoke-field {
          position: absolute;
          left: 50%;
          bottom: 4%;
          width: 640px;
          max-width: 90vw;
          height: 240px;
          transform: translateX(-50%);
          pointer-events: none;
        }
        .intro-smoke-puff {
          position: absolute;
          bottom: 0;
          width: 130px;
          height: 130px;
          border-radius: 50%;
          background:
            radial-gradient(circle at 40% 40%,
              rgba(255,235,215,0.55) 0%,
              rgba(220,200,190,0.35) 30%,
              rgba(140,130,150,0.18) 60%,
              rgba(60,55,80,0) 80%);
          filter: blur(10px);
          opacity: 0;
          animation-name: intro-smoke;
          animation-timing-function: cubic-bezier(0.2, 0.6, 0.3, 1);
          animation-fill-mode: forwards;
          animation-iteration-count: infinite;
        }
        .intro-smoke-wide {
          position: absolute;
          bottom: -18px;
          width: 240px;
          height: 90px;
          border-radius: 50%;
          background: radial-gradient(ellipse at center,
            rgba(240,220,205,0.35) 0%,
            rgba(180,170,180,0.18) 40%,
            rgba(60,55,80,0) 75%);
          filter: blur(14px);
          opacity: 0;
          animation: intro-smoke-wide 2.6s ease-out infinite;
        }
        @keyframes intro-smoke {
          0%   { transform: translate(-50%, 20px) scale(0.35); opacity: 0; }
          15%  { opacity: 0.95; }
          70%  { opacity: 0.7; }
          100% { transform: translate(-50%, -60px) scale(2.6); opacity: 0; }
        }
        @keyframes intro-smoke-wide {
          0%   { transform: scale(0.6);  opacity: 0; }
          20%  { opacity: 0.7; }
          100% { transform: scale(2.2);  opacity: 0; }
        }

        /* Flame — three concentric layers + sparks */
        .intro-flame-wrap {
          position: absolute;
          top: 100%;
          left: 50%;
          margin-top: -10px;
          width: 80px;
          height: 200px;
          transform: translateX(-50%);
          animation: intro-flame-grow 0.45s ease-out forwards,
                     intro-flame-flick 0.06s steps(2) infinite 0.45s;
          transform-origin: 50% 0%;
          pointer-events: none;
        }
        @keyframes intro-flame-grow {
          from { transform: translateX(-50%) scaleY(0.12); }
          to   { transform: translateX(-50%) scaleY(1); }
        }
        @keyframes intro-flame-flick {
          0%   { transform: translateX(-50%) scaleY(1)    scaleX(1); }
          100% { transform: translateX(-51%) scaleY(1.05) scaleX(0.94); }
        }
        .intro-flame-outer {
          position: absolute;
          top: 0; left: 50%;
          transform: translateX(-50%);
          width: 78px; height: 180px;
          border-radius: 50% 50% 40% 40% / 24% 24% 76% 76%;
          background: radial-gradient(ellipse at 50% 18%,
            rgba(255, 230, 160, 0.65) 0%,
            rgba(255, 170, 60, 0.55) 30%,
            rgba(255, 90, 30, 0.4) 60%,
            rgba(180, 30, 0, 0.15) 85%,
            rgba(0,0,0,0) 100%);
          filter: blur(6px);
        }
        .intro-flame-mid {
          position: absolute;
          top: 2px; left: 50%;
          transform: translateX(-50%);
          width: 44px; height: 140px;
          border-radius: 50% 50% 40% 40% / 26% 26% 74% 74%;
          background: radial-gradient(ellipse at 50% 18%,
            #fff5c8 0%,
            #ffd85c 25%,
            #ff8a1f 55%,
            #ff3d00 85%,
            rgba(255,61,0,0) 100%);
          filter: blur(2px) drop-shadow(0 0 26px rgba(255, 138, 31, 0.85));
        }
        .intro-flame-core {
          position: absolute;
          top: 4px; left: 50%;
          transform: translateX(-50%);
          width: 20px; height: 90px;
          border-radius: 50% 50% 40% 40% / 30% 30% 70% 70%;
          background: radial-gradient(ellipse at 50% 20%,
            #ffffff 0%,
            #fff2b5 40%,
            #ffb347 78%,
            rgba(255,138,31,0) 100%);
          filter: blur(0.5px);
        }
        .intro-spark {
          position: absolute;
          bottom: 0;
          width: 3px; height: 3px;
          border-radius: 50%;
          background: radial-gradient(circle, #fff2b5 0%, #ffb347 60%, rgba(255,138,31,0) 100%);
          box-shadow: 0 0 6px #ffcc66;
          opacity: 0;
          animation: intro-spark linear infinite;
        }
        @keyframes intro-spark {
          0%   { transform: translateY(-4px)  scale(1);   opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(160px) scale(0.4); opacity: 0; }
        }

        /* Warp streaks */
        .intro-streak {
          position: absolute;
          top: -20%;
          height: 22vh;
          background: linear-gradient(180deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.9) 50%,
            rgba(212,175,55,0.6) 100%);
          border-radius: 999px;
          filter: drop-shadow(0 0 6px rgba(255,255,255,0.6));
          animation: intro-warp linear infinite;
          opacity: 0;
        }
        @keyframes intro-warp {
          0%   { transform: translateY(-30vh) scaleY(0.4); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(135vh) scaleY(1.8); opacity: 0; }
        }

        /* Overlay fade-out */
        .intro-fadeout {
          animation: intro-fade 0.7s ease-out forwards;
        }
        @keyframes intro-fade {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/** Realistic Falcon-inspired rocket illustration */
function RocketSVG() {
  return (
    <svg
      width="140"
      height="320"
      viewBox="0 0 140 320"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        filter:
          "drop-shadow(0 20px 40px rgba(0,0,0,0.55)) drop-shadow(0 0 22px rgba(255,180,90,0.15))",
      }}
    >
      <defs>
        {/* Fuselage — cylindrical highlight with cool white core and darker edges */}
        <linearGradient id="fuselage" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#6a6d75" />
          <stop offset="12%"  stopColor="#c8ccd2" />
          <stop offset="40%"  stopColor="#ffffff" />
          <stop offset="60%"  stopColor="#f5f6f8" />
          <stop offset="88%"  stopColor="#a0a4ac" />
          <stop offset="100%" stopColor="#4a4d55" />
        </linearGradient>
        <linearGradient id="nose" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#4a4d55" />
          <stop offset="45%"  stopColor="#e8ebef" />
          <stop offset="100%" stopColor="#3a3d45" />
        </linearGradient>
        <linearGradient id="engine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#111418" />
          <stop offset="45%"  stopColor="#4a4d55" />
          <stop offset="100%" stopColor="#0a0c10" />
        </linearGradient>
        <linearGradient id="fin" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#2a2d33" />
          <stop offset="60%"  stopColor="#6a6d75" />
          <stop offset="100%" stopColor="#1a1c22" />
        </linearGradient>
        <radialGradient id="window" cx="0.35" cy="0.3" r="0.9">
          <stop offset="0%"   stopColor="#e9f6ff" />
          <stop offset="45%"  stopColor="#3f92c9" />
          <stop offset="100%" stopColor="#0a2b4a" />
        </radialGradient>
        <linearGradient id="nozzle" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2a2d33" />
          <stop offset="60%"  stopColor="#0a0c10" />
          <stop offset="100%" stopColor="#050608" />
        </linearGradient>
      </defs>

      {/* NOSE CONE */}
      <path
        d="M70 6 C 84 34, 92 68, 92 96 L 48 96 C 48 68, 56 34, 70 6 Z"
        fill="url(#nose)"
        stroke="#22252b"
        strokeWidth="1"
      />
      {/* Nose highlight */}
      <path
        d="M70 10 C 78 34, 82 62, 82 94 L 74 94 C 74 62, 72 34, 70 10 Z"
        fill="rgba(255,255,255,0.4)"
      />
      {/* Nose ring */}
      <line x1="50" y1="96" x2="90" y2="96" stroke="#2a2d33" strokeWidth="1.2" />

      {/* UPPER STAGE */}
      <rect x="46" y="96" width="48" height="60" fill="url(#fuselage)" />
      {/* Stage separator */}
      <rect x="45" y="154" width="50" height="4" fill="#2a2d33" />
      <rect x="45" y="156" width="50" height="1" fill="#1a1c22" />

      {/* MAIN BODY */}
      <rect x="44" y="158" width="52" height="112" fill="url(#fuselage)" />
      {/* Vertical panel seams */}
      <line x1="60" y1="98" x2="60" y2="268" stroke="#9aa0a8" strokeWidth="0.5" opacity="0.6" />
      <line x1="80" y1="98" x2="80" y2="268" stroke="#9aa0a8" strokeWidth="0.5" opacity="0.6" />
      {/* Horizontal band around mid-body */}
      <rect x="44" y="196" width="52" height="3" fill="#dcdfe4" />
      <rect x="44" y="199" width="52" height="1" fill="#2a2d33" opacity="0.5" />

      {/* Window / porthole */}
      <circle cx="70" cy="126" r="9" fill="#0a0c10" />
      <circle cx="70" cy="126" r="7.5" fill="url(#window)" />
      <circle cx="66.5" cy="122.5" r="2.2" fill="rgba(255,255,255,0.85)" />

      {/* TEURALINK wordmark strip */}
      <rect x="47" y="176" width="46" height="14" rx="1.5" fill="#0e1116" />
      <text
        x="70"
        y="186"
        textAnchor="middle"
        fontFamily="Space Mono, monospace"
        fontSize="7.5"
        fill="#d4af37"
        letterSpacing="1.6"
      >
        TEURALINK
      </text>

      {/* Flag stripe accent */}
      <rect x="47" y="210" width="46" height="6" fill="#0a0c10" />
      <rect x="47" y="210" width="46" height="2" fill="#d4af37" />
      <rect x="47" y="214" width="46" height="2" fill="#8b6b17" opacity="0.7" />

      {/* Rivets column */}
      {[102, 118, 138, 158, 218, 238, 258].map((y, idx) => (
        <React.Fragment key={idx}>
          <circle cx="48" cy={y} r="0.9" fill="#7c8088" />
          <circle cx="92" cy={y} r="0.9" fill="#7c8088" />
        </React.Fragment>
      ))}

      {/* Registration text */}
      <text
        x="70"
        y="246"
        textAnchor="middle"
        fontFamily="Space Mono, monospace"
        fontSize="4.5"
        fill="#3a3d45"
        letterSpacing="1"
      >
        TL-01 / 2026
      </text>

      {/* GRID FIN (small, top of main body) */}
      <rect x="34" y="164" width="10" height="18" fill="#3a3d45" stroke="#1a1c22" strokeWidth="0.6" />
      <rect x="96" y="164" width="10" height="18" fill="#3a3d45" stroke="#1a1c22" strokeWidth="0.6" />
      {/* Fin grid pattern */}
      {[0, 1, 2].map((r) => (
        <React.Fragment key={`gf-${r}`}>
          <line x1="34" y1={168 + r * 4.5} x2="44" y2={168 + r * 4.5} stroke="#1a1c22" strokeWidth="0.4" />
          <line x1="96" y1={168 + r * 4.5} x2="106" y2={168 + r * 4.5} stroke="#1a1c22" strokeWidth="0.4" />
        </React.Fragment>
      ))}
      {[0, 1].map((c) => (
        <React.Fragment key={`gv-${c}`}>
          <line x1={37 + c * 3.5} y1="164" x2={37 + c * 3.5} y2="182" stroke="#1a1c22" strokeWidth="0.4" />
          <line x1={99 + c * 3.5} y1="164" x2={99 + c * 3.5} y2="182" stroke="#1a1c22" strokeWidth="0.4" />
        </React.Fragment>
      ))}

      {/* LOWER FINS */}
      <path
        d="M44 246 L 18 296 L 44 274 Z"
        fill="url(#fin)"
        stroke="#0e1116"
        strokeWidth="1"
      />
      <path
        d="M96 246 L 122 296 L 96 274 Z"
        fill="url(#fin)"
        stroke="#0e1116"
        strokeWidth="1"
      />
      {/* Fin trim accents */}
      <line x1="44" y1="246" x2="18" y2="296" stroke="#d4af37" strokeWidth="0.6" opacity="0.5" />
      <line x1="96" y1="246" x2="122" y2="296" stroke="#d4af37" strokeWidth="0.6" opacity="0.5" />

      {/* ENGINE SECTION */}
      <path
        d="M44 268 L 96 268 L 92 290 L 48 290 Z"
        fill="url(#engine)"
        stroke="#0a0c10"
        strokeWidth="1"
      />
      {/* Bolts on engine bay */}
      <circle cx="50" cy="273" r="1" fill="#7c8088" />
      <circle cx="90" cy="273" r="1" fill="#7c8088" />
      <circle cx="50" cy="285" r="1" fill="#7c8088" />
      <circle cx="90" cy="285" r="1" fill="#7c8088" />

      {/* NOZZLE BELL (Merlin-inspired) */}
      <path
        d="M48 290 L 92 290 L 100 314 L 40 314 Z"
        fill="url(#nozzle)"
        stroke="#0a0c10"
        strokeWidth="1"
      />
      {/* Nozzle ridges */}
      <line x1="52" y1="296" x2="88" y2="296" stroke="#3a3d45" strokeWidth="0.6" />
      <line x1="50" y1="302" x2="90" y2="302" stroke="#3a3d45" strokeWidth="0.6" />
      <line x1="47" y1="308" x2="93" y2="308" stroke="#3a3d45" strokeWidth="0.6" />

      {/* Inner nozzle mouth (dark ellipse) */}
      <ellipse cx="70" cy="314" rx="30" ry="4" fill="#050608" />
      <ellipse cx="70" cy="313" rx="28" ry="3.2" fill="#1a1c22" opacity="0.9" />

      {/* Subtle body highlight streak (top-left light) */}
      <rect x="52" y="98" width="4" height="170" fill="rgba(255,255,255,0.35)" />
      <rect x="86" y="98" width="2" height="170" fill="rgba(0,0,0,0.2)" />
    </svg>
  );
}
